import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService } from '../catalog/catalog.service';
import {
  AllocationPolicyEvaluationRecord,
  CreateAllocationPolicyPayload,
  EvaluateAllocationPayload,
  SalesApiService,
  SalesChannelRecord,
  UpdateAllocationPolicyPayload
} from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-allocation-policy-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    InputNumberModule, SelectModule, TableModule, CustomerSelectorComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.allocations.form.createTitle') : t('sales.allocations.form.editTitle')" [description]="t('sales.allocations.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <header class="card header">
          <div class="toolbar">
            <a class="back-link" [routerLink]="['/sales/allocation-policies']">{{ t('catalog.action.backToList') }}</a>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
          </div>
          <div class="summary">
            <span>{{ t('sales.allocations.fields.code') }}: <strong>{{ form.code || '-' }}</strong></span>
            <span>{{ t('sales.allocations.fields.scopeType') }}: <strong>{{ scopeLabel(form.scopeType) }}</strong></span>
            <span>{{ t('sales.allocations.fields.maxQuantity') }}: <strong>{{ form.maxReservedQuantity }}</strong></span>
          </div>
        </header>

        @if (isCreateMode()) {
          <section class="card grid">
            <label><span>{{ t('sales.allocations.fields.code') }}</span><input pInputText [(ngModel)]="form.code" /></label>
            <label><span>{{ t('sales.allocations.fields.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
            <label>
              <span>{{ t('sales.allocations.fields.scopeType') }}</span>
              <p-select [options]="scopeOptions" optionValue="value" [(ngModel)]="form.scopeType" appendTo="body" (ngModelChange)="form.scopeId = ''">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            @if (form.scopeType === 'Channel') {
              <label>
                <span>{{ t('sales.allocations.fields.scopeTarget') }}</span>
                <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.scopeId" appendTo="body" />
              </label>
            } @else if (form.scopeType === 'Offering') {
              <label>
                <span>{{ t('sales.allocations.fields.scopeTarget') }}</span>
                <p-select [options]="offeringOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.scopeId" [filter]="true" appendTo="body" />
              </label>
            }
            <label><span>{{ t('sales.allocations.fields.maxQuantity') }}</span><p-inputNumber [(ngModel)]="form.maxReservedQuantity" [min]="0" /></label>
            <label><span>{{ t('sales.allocations.fields.maxAmount') }}</span><p-inputNumber [(ngModel)]="form.maxReservedAmount" [min]="0" /></label>
            <label><span>{{ t('sales.allocations.fields.priority') }}</span><p-inputNumber [(ngModel)]="form.priority" [min]="0" /></label>
            <label class="check"><input type="checkbox" [(ngModel)]="form.requireQuotaPass" /><span>{{ t('sales.allocations.fields.requireQuotaPass') }}</span></label>
          </section>
        } @else {
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'general'" (click)="activeTab.set('general')">{{ t('sales.allocations.tabs.general') }}</button>
            <button type="button" [class.active]="activeTab() === 'evaluate'" (click)="activeTab.set('evaluate')">{{ t('sales.allocations.tabs.evaluate') }}</button>
          </div>

          @if (activeTab() === 'general') {
            <section class="card grid">
              <label><span>{{ t('sales.allocations.fields.code') }}</span><input pInputText [(ngModel)]="form.code" readonly /></label>
              <label><span>{{ t('sales.allocations.fields.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
              <label><span>{{ t('sales.allocations.fields.scopeType') }}</span><input pInputText [ngModel]="scopeLabel(form.scopeType)" readonly /></label>
              <label><span>{{ t('sales.allocations.fields.maxQuantity') }}</span><p-inputNumber [(ngModel)]="form.maxReservedQuantity" [min]="0" /></label>
              <label><span>{{ t('sales.allocations.fields.maxAmount') }}</span><p-inputNumber [(ngModel)]="form.maxReservedAmount" [min]="0" /></label>
              <label><span>{{ t('sales.allocations.fields.priority') }}</span><p-inputNumber [(ngModel)]="form.priority" [min]="0" /></label>
              <label class="check"><input type="checkbox" [(ngModel)]="form.requireQuotaPass" /><span>{{ t('sales.allocations.fields.requireQuotaPass') }}</span></label>
              <label class="check"><input type="checkbox" [(ngModel)]="form.isActive" /><span>{{ t('sales.allocations.active') }}</span></label>
            </section>
          }

          @if (activeTab() === 'evaluate') {
            <section class="card grid">
              <label>
                <span>{{ t('sales.orders.fields.channel') }}</span>
                <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="evaluateForm.salesChannelId" [showClear]="true" appendTo="body" />
              </label>
              <label><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="evaluateForm.customerAccountId" /></label>
              <label><span>{{ t('sales.allocations.evaluate.quantity') }}</span><p-inputNumber [(ngModel)]="evaluateForm.quantity" [min]="0" /></label>
              <label><span>{{ t('sales.allocations.evaluate.amount') }}</span><p-inputNumber [(ngModel)]="evaluateForm.amount" [min]="0" /></label>
              <label>
                <span>{{ t('sales.reservations.fields.offering') }}</span>
                <p-select [options]="offeringOptions()" optionLabel="label" optionValue="value" [(ngModel)]="evaluateForm.offeringId" [showClear]="true" [filter]="true" appendTo="body" />
              </label>
              <div class="evaluate-action">
                <button pButton type="button" [label]="t('sales.allocations.evaluate.run')" [disabled]="evaluating()" (click)="runEvaluation()"></button>
              </div>
            </section>

            @if (evaluationResult()) {
              <section class="card">
                <p class="result" [class.denied]="!evaluationResult()!.allowed">{{ evaluationResult()!.allowed ? t('sales.allocations.evaluate.allowed') : t('sales.allocations.evaluate.denied') }}</p>
                @if (evaluationResult()!.violations.length) {
                  <ul class="violations">
                    @for (violation of evaluationResult()!.violations; track violation) {
                      <li>{{ violation }}</li>
                    }
                  </ul>
                }
                <p-table [value]="evaluationResult()!.policies" [paginator]="true" [rows]="10">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('sales.allocations.fields.code') }}</th>
                      <th>{{ t('sales.allocations.evaluate.activeQuantity') }}</th>
                      <th>{{ t('sales.allocations.evaluate.proposedQuantity') }}</th>
                      <th>{{ t('sales.allocations.fields.maxQuantity') }}</th>
                      <th>{{ t('columns.status') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.code }}</td>
                      <td>{{ row.activeReservedQuantity }}</td>
                      <td>{{ row.proposedQuantity }}</td>
                      <td>{{ row.maxReservedQuantity }}</td>
                      <td>{{ row.allowed ? t('sales.allocations.evaluate.allowed') : t('sales.allocations.evaluate.denied') }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              </section>
            }
          }
        }

        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; justify-content: space-between; align-items: center; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; margin-bottom: 10px; }
    .header .summary { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 10px; font-size: 13px; color: #475569; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.check { grid-template-columns: auto 1fr; align-items: center; }
    .evaluate-action { display: flex; align-items: end; }
    .tabs { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
    .tabs button { border: 1px solid #cbd5e1; background: #fff; border-radius: 999px; padding: 6px 12px; cursor: pointer; }
    .tabs button.active { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
    .result { font-weight: 600; margin: 0 0 8px; color: #047857; }
    .result.denied { color: #b91c1c; }
    .violations { margin: 0 0 10px; padding-inline-start: 18px; color: #b91c1c; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class SalesAllocationPolicyDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly catalog = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly evaluating = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly activeTab = signal<'general' | 'evaluate'>('general');
  readonly channels = signal<SalesChannelRecord[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly evaluationResult = signal<{
    allowed: boolean;
    violations: string[];
    policies: AllocationPolicyEvaluationRecord[];
    decisionId: string;
  } | null>(null);

  readonly scopeOptions = [
    { value: 'Global', labelKey: 'sales.allocations.scopes.global' },
    { value: 'Channel', labelKey: 'sales.allocations.scopes.channel' },
    { value: 'Offering', labelKey: 'sales.allocations.scopes.offering' }
  ];

  form = {
    code: '',
    name: '',
    scopeType: 'Global',
    scopeId: '' as string | null,
    maxReservedQuantity: 100,
    maxReservedAmount: null as number | null,
    requireQuotaPass: false,
    priority: 100,
    isActive: true
  };

  evaluateForm = {
    salesChannelId: '' as string | null,
    customerAccountId: '' as string | null,
    offeringId: '' as string | null,
    quantity: 1,
    amount: 0
  };

  ngOnInit(): void {
    this.api.listChannels().subscribe({
      next: rows => this.channels.set(rows),
      error: () => this.channels.set([])
    });
    this.catalog.listOfferings().subscribe({
      next: rows => this.offerings.set(rows),
      error: () => this.offerings.set([])
    });

    const policyId = this.route.snapshot.paramMap.get('policyId');
    this.isCreateMode.set(!policyId || policyId === 'new');
    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }
    this.loadPolicy(policyId!);
  }

  channelOptions(): { label: string; value: string }[] {
    return this.channels().map(item => ({ label: `${item.code} - ${item.name}`, value: item.id }));
  }

  offeringOptions(): { label: string; value: string }[] {
    return this.offerings().map(item => ({ label: `${item.code} - ${item.title}`, value: item.id }));
  }

  loadPolicy(id: string): void {
    this.api.getAllocationPolicy(id).subscribe({
      next: policy => {
        this.form = {
          code: policy.code,
          name: policy.name,
          scopeType: policy.scopeType,
          scopeId: policy.scopeId ?? null,
          maxReservedQuantity: policy.maxReservedQuantity,
          maxReservedAmount: policy.maxReservedAmount ?? null,
          requireQuotaPass: policy.requireQuotaPass,
          priority: policy.priority,
          isActive: policy.isActive
        };
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('sales.allocations.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  save(): void {
    this.saving.set(true);
    this.errorMessage.set('');

    if (this.isCreateMode()) {
      const payload: CreateAllocationPolicyPayload = {
        code: this.form.code,
        name: this.form.name,
        scopeType: this.form.scopeType,
        scopeId: this.form.scopeType === 'Global' ? null : (this.form.scopeId || null),
        maxReservedQuantity: this.form.maxReservedQuantity,
        maxReservedAmount: this.form.maxReservedAmount,
        requireQuotaPass: this.form.requireQuotaPass,
        priority: this.form.priority
      };
      this.api.createAllocationPolicy(payload).subscribe({
        next: policy => void this.router.navigate(['/sales/allocation-policies', policy.id]),
        error: () => {
          this.errorMessage.set(this.t('sales.allocations.errorSave'));
          this.saving.set(false);
        }
      });
      return;
    }

    const id = this.route.snapshot.paramMap.get('policyId')!;
    const payload: UpdateAllocationPolicyPayload = {
      name: this.form.name,
      maxReservedQuantity: this.form.maxReservedQuantity,
      maxReservedAmount: this.form.maxReservedAmount,
      requireQuotaPass: this.form.requireQuotaPass,
      priority: this.form.priority,
      isActive: this.form.isActive
    };
    this.api.updateAllocationPolicy(id, payload).subscribe({
      next: () => this.saving.set(false),
      error: () => {
        this.errorMessage.set(this.t('sales.allocations.errorSave'));
        this.saving.set(false);
      }
    });
  }

  runEvaluation(): void {
    this.evaluating.set(true);
    this.errorMessage.set('');
    const payload: EvaluateAllocationPayload = {
      salesChannelId: this.evaluateForm.salesChannelId || null,
      customerAccountId: this.evaluateForm.customerAccountId || null,
      currencyCode: 'IRR',
      correlationKey: `eval-${Date.now()}`,
      lines: [{
        offeringId: this.evaluateForm.offeringId || null,
        quantity: this.evaluateForm.quantity,
        amount: this.evaluateForm.amount
      }]
    };
    this.api.evaluateAllocation(payload).subscribe({
      next: result => {
        this.evaluationResult.set(result);
        this.evaluating.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('sales.allocations.evaluate.error'));
        this.evaluating.set(false);
      }
    });
  }

  scopeLabel(scopeType: string): string {
    return this.localization.translate(`sales.allocations.scopes.${scopeType.charAt(0).toLowerCase()}${scopeType.slice(1)}`);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
